import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import StoryCard from "@/components/feature/StoryCard";
import useSeo from "@/hooks/useSeo";
import { shortStories } from "@/mocks/narutoContent";

export default function ShortStory() {
  const { id } = useParams();
  const story = shortStories.find((s) => s.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  // ===== 列表视图 =====
  if (!story) {
    useSeo({
      title: "火影忍者同人短篇速读 - 短篇推荐 | 影页社",
      description:
        "影页社火影忍者同人短篇速读栏目，每日精选一篇走心同人短篇，围绕治愈、羁绊、亲情与梦想展开，五六分钟即可读完，适合通勤与碎片时间阅读。",
      keywords: "火影忍者同人短篇,火影忍者同人文,火影同人速读,火影同人推荐",
      canonical: "https://hokagecell.github.io/duanpian",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "影页社 · 火影忍者同人短篇速读",
          url: "https://hokagecell.github.io/duanpian",
        },
      ],
    });

    return (
      <AppLayout>
        <div className="pt-20">
          <Breadcrumb items={[{ name: "短篇速读" }]} />
          <header className="mx-auto w-full max-w-7xl px-4 md:px-6 pt-6 pb-8">
            <h1 className="font-display text-3xl md:text-4xl text-foreground-950">短篇速读 · 每日精选</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              每日从同好投稿中精选一篇走心短篇，五六分钟读完。治愈、热血、亲情与羁绊，都藏在字里行间。
            </p>
          </header>
          <div className="mx-auto w-full max-w-5xl px-4 md:px-6 pb-16 space-y-5">
            {shortStories.map((s, i) => (
              <StoryCard key={s.id} story={s} index={i} />
            ))}
          </div>
        </div>
      </AppLayout>
    );
  }

  // ===== 详情视图 =====
  const related = shortStories.filter((s) => s.id !== story.id).slice(0, 2);
  const words = story.paragraphs.join("").length;

  useSeo({
    title: `${story.title} - 火影忍者同人短篇速读 | 影页社`,
    description: story.summary,
    keywords: `火影忍者同人短篇,${story.title},火影同人文`,
    canonical: `https://hokagecell.github.io/duanpian/${story.id}`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: story.title,
        description: story.summary,
        author: { "@type": "Person", name: story.author },
        articleSection: story.category,
        inLanguage: "zh-CN",
        wordCount: words,
        url: `https://hokagecell.github.io/duanpian/${story.id}`,
        image: story.cover,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://hokagecell.github.io/" },
          { "@type": "ListItem", position: 2, name: "短篇速读", item: "https://hokagecell.github.io/duanpian" },
          { "@type": "ListItem", position: 3, name: story.title, item: `https://hokagecell.github.io/duanpian/${story.id}` },
        ],
      },
    ],
  });

  return (
    <AppLayout>
      <div className="pt-20">
        <Breadcrumb items={[{ name: "短篇速读", path: "/duanpian" }, { name: story.title }]} />

        <article className="mx-auto w-full max-w-3xl px-4 md:px-6 pt-6 pb-12">
          <header className="text-center">
            <div className="flex flex-wrap items-center justify-center gap-2 font-label text-xs">
              <span className="rounded-full bg-primary-100 px-3 py-1 text-primary-700">{story.category}</span>
              <span className="inline-flex items-center gap-1 text-foreground-500"><i className="ri-time-line"></i>{story.readTime} 阅读</span>
              <span className="inline-flex items-center gap-1 text-foreground-500"><i className="ri-account-circle-line"></i>{story.author}</span>
            </div>
            <h1 className="mt-5 font-display text-3xl md:text-5xl text-foreground-950 leading-tight">{story.title}</h1>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-foreground-600">{story.summary}</p>
          </header>

          <div className="mt-8 overflow-hidden rounded-lg border border-background-200">
            <img
              src={story.cover}
              alt={`${story.title} 火影忍者同人短篇插画`}
              title={`${story.title} · 火影忍者同人短篇`}
              className="h-64 w-full object-cover object-top md:h-80"
            />
          </div>

          <div className="mt-8 space-y-5">
            {story.paragraphs.map((p, i) => (
              <p key={i} className="text-base leading-loose text-foreground-800 first-letter:text-primary-700 first-letter:font-display first-letter:text-2xl">
                {p}
              </p>
            ))}
          </div>

          <footer className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-6">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-primary-100 font-display text-xl text-primary-700">
                {story.author[0]}
              </span>
              <div>
                <p className="font-label text-sm text-foreground-900">本文作者 · {story.author}</p>
                <p className="font-label text-xs text-foreground-500">影页社 · 同人短篇速读</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" className="grid h-10 w-10 place-items-center rounded-full border border-background-200 text-foreground-600 transition-colors hover:bg-accent-500 hover:text-background-50" aria-label="点赞">
                <i className="ri-heart-3-line"></i>
              </button>
              <button type="button" className="grid h-10 w-10 place-items-center rounded-full border border-background-200 text-foreground-600 transition-colors hover:bg-primary-500 hover:text-background-50" aria-label="分享">
                <i className="ri-share-line"></i>
              </button>
            </div>
          </footer>
        </article>

        {/* 相关推荐 */}
        <section className="bg-background-100/70">
          <div className="mx-auto w-full max-w-5xl px-4 md:px-6 py-12">
            <h2 className="mb-6 font-display text-2xl text-foreground-950">继续阅读 · 相关短篇</h2>
            <div className="space-y-5">
              {related.map((s, i) => (
                <StoryCard key={s.id} story={s} index={i} />
              ))}
            </div>
          </div>
        </section>
      </div>
    </AppLayout>
  );
}